// BQ SyncSpec: C3030 Ceiling Assemblies Quantity Calculator - 2025 Updated

export const calculateQuantities = (data) => {
  const length = parseFloat(data.lengthM) || 0;
  const width = parseFloat(data.widthM) || 0;
  const area = length * width;
  const perimeter = 2 * (length + width);

  // CONSTANTS
  const FURRING_SPACING = 0.40; // m
  const SIDE_DROP_DEPTH = 0.45; // m
  const TRAY_OFFSET = 0.60; // m

  // BASELINE RATIOS (per m² unless noted)
  const BASELINES = {
    wallAngle: Math.max(1.7 * area, perimeter), // lm
    carryingChannel: 1.70, // lm/m²
    doubleFurring: 3.00, // lm/m²
    hangers: 2.10, // pcs/m²
    wClips: 2.10, // pcs/m²
    ceilingSurface: 1.10, // m²/m²
    paint: 1.10, // m²/m²
    jointTapes: 4.66, // lm/m²
    screws: 22, // pcs/m²
    jointCompound: 0.90 // kg/m²
  };

  // TYPE MULTIPLIERS
  const getTypeMultipliers = (ceilingType) => {
    switch (ceilingType) {
      case 'ceiling_painted_slab':
        return {
          carryingChannel: 0,
          doubleFurring: 0,
          hangers: 0,
          wClips: 0,
          ceilingSurface: 0,
          paint: 1,
          jointTapes: 0,
          screws: 0,
          jointCompound: 0
        };
      case 'ceiling_flat':
        return {
          carryingChannel: 1,
          doubleFurring: 1,
          hangers: 1,
          wClips: 1,
          ceilingSurface: 1,
          paint: 1,
          jointTapes: 1,
          screws: 1,
          jointCompound: 1
        };
      case 'ceiling_suspended':
        return {
          carryingChannel: 1,
          doubleFurring: 1,
          hangers: 1.05,
          wClips: 1.05,
          ceilingSurface: 1,
          paint: 1,
          jointTapes: 1,
          screws: 1,
          jointCompound: 1
        };
      default:
        return {
          carryingChannel: 1,
          doubleFurring: 1,
          hangers: 1,
          wClips: 1,
          ceilingSurface: 1,
          paint: 1,
          jointTapes: 1,
          screws: 1,
          jointCompound: 1
        };
    }
  };

  const typeMultipliers = getTypeMultipliers(data.ceilingType);

  // Helper functions
  const innerP = (offset) => 2 * ((length - 2 * offset) + (width - 2 * offset));
  const N_fc = (run) => run / FURRING_SPACING;

  // Calculate base quantities
  let wallAngle = BASELINES.wallAngle;
  let carryingChannel = BASELINES.carryingChannel * area * typeMultipliers.carryingChannel;
  let doubleFurring = BASELINES.doubleFurring * area * typeMultipliers.doubleFurring;
  let hangers = BASELINES.hangers * area * typeMultipliers.hangers;
  let wClips = BASELINES.wClips * area * typeMultipliers.wClips;
  let ceilingSurface = BASELINES.ceilingSurface * area * typeMultipliers.ceilingSurface;
  let paint = BASELINES.paint * area * typeMultipliers.paint;
  let jointTapes = BASELINES.jointTapes * area * typeMultipliers.jointTapes;
  let screws = BASELINES.screws * area * typeMultipliers.screws;
  let jointCompound = BASELINES.jointCompound * area * typeMultipliers.jointCompound;

  // Apply accent configurations
  switch (data.accentConfig) {
    case 'accent_shadow_gap':
      wallAngle = perimeter; // Replace with Double Wall Angle
      break;
    case 'accent_curtain_gap':
      wallAngle = 2 * perimeter;
      doubleFurring += 2 * SIDE_DROP_DEPTH * N_fc(perimeter);
      break;
    case 'accent_recessed_cove':
      if (data.accentSubtype === 'ac03_01_single_linear') {
        doubleFurring += 1.5 * (2 * SIDE_DROP_DEPTH * N_fc(length));
      } else if (data.accentSubtype === 'ac03_02_double_linear') {
        doubleFurring += 1.5 * (2 * SIDE_DROP_DEPTH * N_fc(2 * length));
      } else if (data.accentSubtype === 'ac03_04_centered') {
        doubleFurring += 1.5 * (2 * SIDE_DROP_DEPTH * N_fc(innerP(TRAY_OFFSET)));
      }
      break;
    case 'accent_tray':
      const innerPerimeter = innerP(TRAY_OFFSET);
      wallAngle = perimeter + innerPerimeter;
      const extraFurring = 2 * SIDE_DROP_DEPTH * N_fc(innerPerimeter);
      doubleFurring += data.accentSubtype === 'ac04_02_cove' ? extraFurring * 1.5 : extraFurring;
      break;
  }

  // Apply allowances if specified
  const allowances = data.materialSpecs?.allowances || {};
  if (allowances.lgsFraming) {
    const allowanceMultiplier = 1 + (parseFloat(allowances.lgsFraming) / 100);
    wallAngle *= allowanceMultiplier;
    carryingChannel *= allowanceMultiplier;
    doubleFurring *= allowanceMultiplier;
    hangers *= allowanceMultiplier;
    wClips *= allowanceMultiplier;
  }

  if (allowances.ceilingSurface) {
    const allowanceMultiplier = 1 + (parseFloat(allowances.ceilingSurface) / 100);
    ceilingSurface *= allowanceMultiplier;
  }

  if (allowances.paintCoatings) {
    const allowanceMultiplier = 1 + (parseFloat(allowances.paintCoatings) / 100);
    paint *= allowanceMultiplier;
    jointTapes *= allowanceMultiplier;
    jointCompound *= allowanceMultiplier;
  }

  // Round results
  return {
    area: parseFloat(area.toFixed(2)),
    perimeter: parseFloat(perimeter.toFixed(2)),
    wallAngle: parseFloat(wallAngle.toFixed(2)),
    carryingChannel: parseFloat(carryingChannel.toFixed(2)),
    doubleFurring: parseFloat(doubleFurring.toFixed(2)),
    hangers: Math.round(hangers),
    wClips: Math.round(wClips),
    ceilingSurface: parseFloat(ceilingSurface.toFixed(2)),
    paint: parseFloat(paint.toFixed(2)),
    jointTapes: parseFloat(jointTapes.toFixed(2)),
    screws: Math.round(screws),
    jointCompound: parseFloat(jointCompound.toFixed(2))
  };
};

// Convert quantities to commercial measurements
export const convertToCommercialQuantities = (quantities, data) => {
  const commercial = {};

  // Get order lengths from material specs
  const getOrderLength = (component) => {
    const orderLength = data.materialSpecs?.[`${component}OrderLength`] || '3.0m';
    return parseFloat(orderLength.replace('m', ''));
  };

  // LGS components: qty / order length = pieces needed
  commercial.hangers = Math.ceil(quantities.hangers); // Already in pieces
  commercial.wallAngle = Math.ceil(quantities.wallAngle / getOrderLength('wallAngle'));
  commercial.carryingChannel = Math.ceil(quantities.carryingChannel / getOrderLength('carryingChannel'));
  commercial.doubleFurring = Math.ceil(quantities.doubleFurring / getOrderLength('furring'));
  commercial.wClips = quantities.wClips; // Already in pieces

  // Ceiling Surface: qty / 2.88 m² = round to whole pieces
  commercial.ceilingSurface = Math.ceil(quantities.ceilingSurface / 2.88);

  // Jointing materials based on order sizes
  const tapeOrderSize = data.materialSpecs?.tapeOrderSize || '75m';
  const tapeRollLength = parseFloat(tapeOrderSize.replace('m', ''));
  commercial.jointingTape = Math.ceil(quantities.jointTapes / tapeRollLength);
  
  // Putty coverage: 1 kg = 15 lm of joints
  commercial.jointingPutty = Math.ceil(quantities.jointTapes / 15); // kg needed
  commercial.sandPaper = Math.ceil(quantities.jointTapes / 2); // 1 pc = 2m joints

  // Paint calculations based on order sizes and coverage
  const baseCoatOrderSize = data.materialSpecs?.baseCoatOrderSize || '4L';
  const finishCoatOrderSize = data.materialSpecs?.finishCoatOrderSize || '4L';
  
  // Base coat coverage: 6 m²/L/coat
  const baseCoatLiters = quantities.paint / 6;
  const baseCoatContainerSize = parseFloat(baseCoatOrderSize.replace('L', ''));
  commercial.paintBase = Math.ceil(baseCoatLiters / baseCoatContainerSize);
  
  // Finish coat coverage: 4 m²/L/coat (2 coats)
  const finishCoatLiters = (quantities.paint * 2) / 4;
  const finishCoatContainerSize = parseFloat(finishCoatOrderSize.replace('L', ''));
  commercial.paintFinish = Math.ceil(finishCoatLiters / finishCoatContainerSize);

  return commercial;
};

// Get material descriptions based on selections
export const getMaterialDescriptions = (data) => {
  const descriptions = {};

  // LGS Components
  if (data.materialSpecs?.hangerType && data.materialSpecs?.hangerDropLength) {
    const typeMap = {
      'solid_rod': 'Solid Rod',
      'threaded_rod': 'Threaded Rod', 
      'suspension_wire': 'Suspension Wire'
    };
    const lengthMap = {
      'upto_300mm': 'Up to 300mm',
      '301_to_600mm': '301 to 600mm',
      '601_to_900mm': '601 to 900mm'
    };
    descriptions.hangers = `${lengthMap[data.materialSpecs.hangerDropLength]} ${typeMap[data.materialSpecs.hangerType]} hanger complete with clips/accessories by ${data.materialSpecs?.lgsBrand === 'geigher' ? 'Geigher' : 'Steel'} or approved equivalent`;
  } else {
    descriptions.hangers = 'Hanger type and drop length not specified';
  }

  if (data.materialSpecs?.wallAngleType && data.materialSpecs?.wallAngleThickness) {
    descriptions.wallAngle = `${data.materialSpecs.wallAngleThickness} Light Gauge steel Wall Angle by ${data.materialSpecs?.lgsBrand === 'geigher' ? 'Geigher' : 'Steel'} or approved equivalent`;
  } else {
    descriptions.wallAngle = 'Wall angle specifications not complete';
  }

  if (data.materialSpecs?.carryingChannelThickness) {
    descriptions.carryingChannel = `${data.materialSpecs.carryingChannelThickness} Light Gauge steel Carrying Channel by ${data.materialSpecs?.lgsBrand === 'geigher' ? 'Geigher' : 'Steel'} or approved equivalent`;
  } else {
    descriptions.carryingChannel = 'Carrying channel thickness not specified';
  }

  if (data.materialSpecs?.furringType && data.materialSpecs?.furringThickness) {
    descriptions.doubleFurring = `${data.materialSpecs.furringThickness} Light Gauge steel Furring by ${data.materialSpecs?.lgsBrand === 'geigher' ? 'Geigher' : 'Steel'} or approved equivalent`;
  } else {
    descriptions.doubleFurring = 'Furring specifications not complete';
  }

  if (data.materialSpecs?.clipsType) {
    const typeMap = {
      'w_clip': 'W Clip',
      'wire_coupling_clip': 'Wire Coupling Clip'
    };
    descriptions.wClips = `${typeMap[data.materialSpecs.clipsType]} furring clip by ${data.materialSpecs?.lgsBrand === 'geigher' ? 'Geigher' : 'Steel'} or approved equivalent`;
  } else {
    descriptions.wClips = 'Clips type not specified';
  }

  // Ceiling Surface
  if (data.materialType === 'gypsum' && data.materialSpecs?.gypsumType && data.materialSpecs?.gypsumThickness) {
    const brandMap = {
      'eastberg': 'Eastberg',
      'alternate_gypsum_1': 'Alternate Gypsum 1',
      'alternate_gypsum_2': 'Alternate Gypsum 2'
    };
    descriptions.ceilingSurface = `${data.materialSpecs.gypsumThickness} ${data.materialSpecs.gypsumType} Ceiling Board Material by ${brandMap[data.materialSpecs.gypsumBrand] || 'Gypsum'} or equivalent, fastened with board screws`;
  } else if (data.materialType === 'fibercement' && data.materialSpecs?.fibercementThickness) {
    const brandMap = {
      'gardner': 'Gardner',
      'alternate_fibercement_1': 'Alternate Fibercement 1',
      'alternate_fibercement_2': 'Alternate Fibercement 2'
    };
    descriptions.ceilingSurface = `${data.materialSpecs.fibercementThickness} Fiber Cement Ceiling Board Material by ${brandMap[data.materialSpecs.fibercementBrand] || 'Fiber Cement'} or equivalent, fastened with board screws`;
  } else if (data.materialType === 'pvc') {
    const brandMap = {
      'main_pvc': 'Main PVC',
      'alternate_pvc_1': 'Alternate PVC 1',
      'alternate_pvc_2': 'Alternate PVC 2'
    };
    descriptions.ceilingSurface = `PVC Ceiling Board Material by ${brandMap[data.materialSpecs.pvcBrand] || 'PVC'} or equivalent, fastened with board screws`;
  } else {
    descriptions.ceilingSurface = 'Ceiling surface material not specified';
  }

  // Jointing materials
  if (data.materialSpecs?.jointingTapeBrand) {
    const brandMap = {
      'main_tape': 'Main Tape',
      'alternate_tape_1': 'Alternate Tape 1',
      'alternate_tape_2': 'Alternate Tape 2'
    };
    descriptions.jointingTape = brandMap[data.materialSpecs.jointingTapeBrand];
  } else {
    descriptions.jointingTape = 'Jointing tape brand not specified';
  }

  if (data.materialSpecs?.jointingPuttyBrand) {
    const brandMap = {
      'main_putty': 'Main Putty',
      'alternate_putty_1': 'Alternate Putty 1',
      'alternate_putty_2': 'Alternate Putty 2'
    };
    descriptions.jointingPutty = brandMap[data.materialSpecs.jointingPuttyBrand];
  } else {
    descriptions.jointingPutty = 'Jointing putty brand not specified';
  }

  // Base Coat (Always Flat White)
  if (data.materialSpecs?.baseCoatBrand) {
    const brandMap = {
      'main_paint': 'Main Paint',
      'alternate_paint_1': 'Alternate Paint 1',
      'alternate_paint_2': 'Alternate Paint 2'
    };
    descriptions.baseCoat = `${brandMap[data.materialSpecs.baseCoatBrand]} Flat White Base Coat`;
  } else {
    descriptions.baseCoat = 'Base coat brand not specified - Flat White';
  }

  // Finish Coat (Top Coat)
  if (data.materialSpecs?.paintBrand && data.materialSpecs?.paintType && data.materialSpecs?.paintTexture) {
    const brandMap = {
      'main_paint': 'Main Paint',
      'alternate_paint_1': 'Alternate Paint 1',
      'alternate_paint_2': 'Alternate Paint 2'
    };
    const typeMap = {
      'premium_interior': 'Premium Interior',
      'standard_interior': 'Standard Interior',
      'eco_friendly': 'Eco-Friendly',
      'budget_interior': 'Budget Interior',
      'economy': 'Economy',
      'basic': 'Basic',
      'value_interior': 'Value Interior',
      'cost_effective': 'Cost-Effective',
      'general_purpose': 'General Purpose'
    };
    const textureMap = {
      'flat': 'Flat',
      'gloss': 'Gloss',
      'semi_gloss': 'Semi Gloss',
      'matt': 'Matt',
      'eggshell': 'Eggshell'
    };
    descriptions.finishCoat = `${brandMap[data.materialSpecs.paintBrand]} ${typeMap[data.materialSpecs.paintType]} ${textureMap[data.materialSpecs.paintTexture]}`;
  } else {
    descriptions.finishCoat = 'Finish coat specifications not complete';
  }

  // Surface Prep
  descriptions.surfacePrep = 'Jointing + Spot Putty + Sanding';

  // Maintain backward compatibility
  descriptions.paint = descriptions.finishCoat;

  return descriptions;
};

// Calculate manhours for activities
export const calculateManhours = (quantities) => {
  return {
    framing: parseFloat((quantities.area * 4).toFixed(1)), // 4 MH/m²
    boards: parseFloat((quantities.area * 2).toFixed(1)), // 2 MH/m²
    surfacePrep: parseFloat((quantities.jointTapes * 1).toFixed(1)), // 1 MH/m
    basePaint: parseFloat((quantities.paint * 1.5).toFixed(1)), // 1.5 MH/m²
    finishPaint: parseFloat((quantities.paint * 2.5).toFixed(1)) // 2.5 MH/m²
  };
};